import { isSameOriginUrl } from '../../subSession/subSessionUrl';
import { getSubSessionHeaders } from './subSessionHeaders';

const requestUrl = (input: RequestInfo | URL): string =>
  typeof Request !== 'undefined' && input instanceof Request ? input.url : String(input);

// Like the worker, only same-origin requests get the header, so a third-party call
// gets no extra CORS preflight.
const isSameOrigin = (input: RequestInfo | URL): boolean => isSameOriginUrl(requestUrl(input));

const withExtraHeaders = (
  headers: HeadersInit | undefined,
  extraHeaders: Record<string, string>,
): HeadersInit => {
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    const merged = new Headers(headers);
    Object.entries(extraHeaders).forEach(([name, value]) => merged.set(name, value));
    return merged;
  }
  const isExtra = (name: string): boolean =>
    Object.keys(extraHeaders).some((extra) => extra.toLowerCase() === name.toLowerCase());
  if (Array.isArray(headers)) {
    return [...headers.filter(([name]) => !isExtra(name)), ...Object.entries(extraHeaders)];
  }
  return {
    ...Object.fromEntries(Object.entries(headers ?? {}).filter(([name]) => !isExtra(name))),
    ...extraHeaders,
  };
};

/**
 * `fetch` for direct calls to the IBE backend, with this tab's sub-session header
 * once the id is confirmed. Use it for every new backend fetch.
 */
export const backendFetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const subSessionHeaders = isSameOrigin(input) ? getSubSessionHeaders() : {};
  if (Object.keys(subSessionHeaders).length === 0) {
    return fetch(input, init);
  }

  const baseHeaders =
    init?.headers ??
    (typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined);

  return fetch(input, { ...init, headers: withExtraHeaders(baseHeaders, subSessionHeaders) });
};
